import { useEffect, useState } from 'react'
import { Loader2, FileText, Upload, AlertCircle, Check } from 'lucide-react'
import { api, type PliCertificate } from '../lib/api'

export interface PliUploadModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (certificate?: PliCertificate) => void
  editingCert?: PliCertificate | null
  /** When true, renders a compact version suitable for inline use (e.g. booking flow) */
  compact?: boolean
}

export function PliUploadModal({
  isOpen,
  onClose,
  onSuccess,
  editingCert,
  compact,
}: PliUploadModalProps) {
  const [file, setFile] = useState<File | null>(null)
  const [insurerName, setInsurerName] = useState(editingCert?.insurerName || '')
  const [policyNumber, setPolicyNumber] = useState(editingCert?.policyNumber || '')
  const [coverageAmount, setCoverageAmount] = useState(editingCert?.coverageAmount || '')
  const [validFrom, setValidFrom] = useState(editingCert?.validFrom?.split('T')[0] || '')
  const [validUntil, setValidUntil] = useState(editingCert?.validUntil?.split('T')[0] || '')
  const [notes, setNotes] = useState(editingCert?.notes || '')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (editingCert) {
      setInsurerName(editingCert.insurerName)
      setPolicyNumber(editingCert.policyNumber || '')
      setCoverageAmount(editingCert.coverageAmount || '')
      setValidFrom(editingCert.validFrom?.split('T')[0] || '')
      setValidUntil(editingCert.validUntil?.split('T')[0] || '')
      setNotes(editingCert.notes || '')
    } else {
      setFile(null)
      setInsurerName('')
      setPolicyNumber('')
      setCoverageAmount('')
      setValidFrom('')
      setValidUntil('')
      setNotes('')
    }
    setError('')
  }, [editingCert, isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!insurerName.trim()) {
      setError('Insurer name is required')
      return
    }

    if (!editingCert && !file) {
      setError('Please select a certificate file')
      return
    }

    setIsSaving(true)
    try {
      if (editingCert) {
        // Update existing
        await api.pli.update(editingCert.id, {
          insurerName: insurerName.trim(),
          policyNumber: policyNumber.trim() || null,
          coverageAmount: coverageAmount.trim() || null,
          validFrom: validFrom || null,
          validUntil: validUntil || null,
          notes: notes.trim() || null,
        })
        onSuccess()
      } else {
        // Upload new
        const formData = new FormData()
        formData.append('file', file!)
        formData.append('insurerName', insurerName.trim())
        if (policyNumber) formData.append('policyNumber', policyNumber.trim())
        if (coverageAmount) formData.append('coverageAmount', coverageAmount.trim())
        if (validFrom) formData.append('validFrom', validFrom)
        if (validUntil) formData.append('validUntil', validUntil)
        if (notes) formData.append('notes', notes.trim())

        const result = await api.pli.upload(formData)
        onSuccess(result.certificate)
      }

      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to save certificate')
    } finally {
      setIsSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {editingCert ? 'Edit Certificate Details' : 'Upload PLI Certificate'}
          </h2>
          {compact && !editingCert && (
            <p className="text-sm text-gray-500 mt-1">
              Upload your Public Liability Insurance certificate to attach it to this booking.
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {!editingCert && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Certificate File *
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary-400 transition-colors">
                <input
                  type="file"
                  accept=".pdf,image/*"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="hidden"
                  id="pli-file-upload"
                />
                <label htmlFor="pli-file-upload" className="cursor-pointer">
                  {file ? (
                    <div className="flex items-center justify-center gap-2 text-primary-600">
                      <FileText className="w-5 h-5" />
                      <span className="text-sm font-medium">{file.name}</span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="w-8 h-8 mx-auto text-gray-400" />
                      <p className="text-sm text-gray-500">Click to upload or drag and drop</p>
                      <p className="text-xs text-gray-400">PDF, JPEG, PNG or WebP (max 10MB)</p>
                    </div>
                  )}
                </label>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Insurance Company *
            </label>
            <input
              type="text"
              value={insurerName}
              onChange={(e) => setInsurerName(e.target.value)}
              placeholder="e.g., Hiscox, AXA, Zurich"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Policy Number</label>
              <input
                type="text"
                value={policyNumber}
                onChange={(e) => setPolicyNumber(e.target.value)}
                placeholder="Optional"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Coverage Amount
              </label>
              <input
                type="text"
                value={coverageAmount}
                onChange={(e) => setCoverageAmount(e.target.value)}
                placeholder="e.g., £5,000,000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valid From</label>
              <input
                type="date"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valid Until</label>
              <input
                type="date"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {!compact && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional information about your coverage"
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              />
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  {editingCert ? 'Update' : 'Upload'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
